/**
 * Google Maps utilities for event location features
 * - Geocoding (address → coordinates)
 * - Navigation deep links
 * - Distance calculation
 * - Google Maps JS API loader
 */

// Default location: Palm Beach County Convention Center
export const DEFAULT_LOCATION = {
  lat: 26.7051169,
  lng: -80.0585432,
  name: 'Palm Beach County Convention Center',
};

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const DEFAULT_LAT = parseFloat(process.env.REACT_APP_DEFAULT_LAT) || DEFAULT_LOCATION.lat;
const DEFAULT_LNG = parseFloat(process.env.REACT_APP_DEFAULT_LNG) || DEFAULT_LOCATION.lng;

// ─── Google Maps Script Loader ───────────────────────────────────────────────

let mapsLoaded = false;
let mapsLoadPromise = null;

export function loadGoogleMapsScript() {
  if (mapsLoaded && window.google?.maps) return Promise.resolve();
  if (mapsLoadPromise) return mapsLoadPromise;

  mapsLoadPromise = new Promise((resolve, reject) => {
    if (window.google?.maps) { mapsLoaded = true; resolve(); return; }
    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY not set'));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => { mapsLoaded = true; resolve(); };
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
  return mapsLoadPromise;
}

// ─── Geocoding ───────────────────────────────────────────────────────────────

const geocodeCache = {};

export async function geocodeAddress(address) {
  if (!address) return null;
  if (geocodeCache[address]) return geocodeCache[address];

  try {
    await loadGoogleMapsScript();
    const geocoder = new window.google.maps.Geocoder();
    const result = await new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          reject(new Error(`Geocode failed: ${status}`));
        }
      });
    });
    geocodeCache[address] = result;
    return result;
  } catch (e) {
    console.warn('[mapUtils] Geocode error:', e.message);
    return null;
  }
}

// ─── Navigation Deep Links ───────────────────────────────────────────────────

export function getGoogleMapsUrl(lat, lng, label) {
  const dest = label ? encodeURIComponent(label) : `${lat},${lng}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&destination_place_id=`;
}

export function getAppleMapsUrl(lat, lng, label) {
  return `https://maps.apple.com/?daddr=${lat},${lng}${label ? `&dname=${encodeURIComponent(label)}` : ''}`;
}

export function getWazeUrl(lat, lng) {
  return `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
}

export function getNavigationUrl(lat, lng, label) {
  // On iOS prefer Apple Maps, otherwise Google Maps
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return isIOS ? getAppleMapsUrl(lat, lng, label) : getGoogleMapsUrl(lat, lng, label);
}

export function openNavigation(lat, lng, label) {
  window.open(getNavigationUrl(lat, lng, label), '_blank');
}

export function showNavigationPicker(lat, lng, label) {
  // Returns array of options for UI to render
  return [
    { label: 'Google Maps', url: getGoogleMapsUrl(lat, lng, label), icon: '🗺️' },
    { label: 'Apple Maps', url: getAppleMapsUrl(lat, lng, label), icon: '🍎' },
    { label: 'Waze', url: getWazeUrl(lat, lng), icon: '🔵' },
  ];
}

// ─── Distance ────────────────────────────────────────────────────────────────

export function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function formatDistance(km) {
  const mi = km * 0.621371;
  if (mi < 0.1) return `${Math.round(mi * 5280)} ft`;
  if (mi < 10) return `${mi.toFixed(1)} mi`;
  return `${Math.round(mi)} mi`;
}

// ─── User Location with Default Fallback ─────────────────────────────────────

export function getUserLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ lat: DEFAULT_LAT, lng: DEFAULT_LNG, isDefault: true });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, isDefault: false });
      },
      () => {
        // Permission denied or error — will prompt user via UI
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  });
}

export function getDefaultLocation() {
  return { lat: DEFAULT_LAT, lng: DEFAULT_LNG, name: DEFAULT_LOCATION.name, isDefault: true };
}
